import { Component } from '@theme/component';

/**
 * @typedef {Object} NotifyMeFormRefs
 * @property {HTMLFormElement} form - The form element
 * @property {HTMLInputElement} emailInput - Email input
 * @property {HTMLInputElement} phoneInput - Phone input (optional)
 * @property {HTMLButtonElement} submitButton - Submit button
 * @property {HTMLElement} buttonLabel - Button label text
 * @property {HTMLElement} [buttonPrice] - Button price text
 * @property {HTMLElement} errorMessage - Error message container
 * @property {HTMLElement} errorText - Error text content
 * @property {HTMLElement} successMessage - Success message container
 * @property {HTMLElement} [legalText] - Legal disclaimer text
 */

/**
 * Notify Me (Back in Stock) form component.
 * 1. Subscribes customer to Klaviyo's native Back in Stock for the variant
 * 2. Subscribes email to marketing email list (if email list ID configured)
 * 3. Subscribes phone to marketing SMS list (if phone provided and SMS list ID configured)
 *
 * @extends {Component<NotifyMeFormRefs>}
 */
class NotifyMeForm extends Component {
  /** @type {string} */
  #originalLabel = '';

  connectedCallback() {
    super.connectedCallback();
    this.#originalLabel = this.refs.buttonLabel?.textContent || '';

    requestAnimationFrame(() => {
      const emailInput = this.refs.emailInput;
      if (!emailInput || emailInput.disabled || this.offsetParent === null) return;
      emailInput.focus({ preventScroll: true });
    });
  }

  /**
   * Handles form submission.
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    const email = this.refs.emailInput?.value?.trim();
    if (!email) return;

    const apiKey = this.dataset.apiKey;
    const variantId = this.dataset.variantId;

    if (!apiKey || !variantId) {
      this.#showError('Back in stock notifications are not configured.');
      return;
    }

    const phone = this.#getFormattedPhone();

    // Disable button while submitting
    this.refs.submitButton.disabled = true;
    const originalLabel = this.refs.buttonLabel.textContent;
    this.refs.buttonLabel.textContent = 'Submitting...';

    try {
      // 1. Subscribe to Klaviyo's native Back in Stock for this variant
      await this.#subscribeBIS(apiKey, variantId, email, phone);

      // 2. Subscribe email to marketing email list
      const emailListId = this.dataset.emailListId;
      if (emailListId) {
        this.#subscribeToList(apiKey, emailListId, email, null, 'Back in Stock — Email Signup');
      }

      // 3. Subscribe phone to SMS marketing list (only if phone provided)
      const smsListId = this.dataset.smsListId;
      if (phone && smsListId) {
        this.#subscribeToList(apiKey, smsListId, null, phone, 'Back in Stock — SMS Signup');
      }

      // 4. Identify with Klaviyo JS if available
      this.#klaviyoIdentify(email);

      this.#showSuccess();
    } catch (error) {
      this.refs.buttonLabel.textContent = originalLabel;
      this.refs.submitButton.disabled = false;
      this.#showError(error.message || 'Something went wrong. Please try again.');
    }
  }

  /**
   * Subscribes customer to Klaviyo's native Back in Stock notifications.
   * @param {string} apiKey
   * @param {string} variantId - Shopify variant ID
   * @param {string} email
   * @param {string|null} phone - E.164 formatted phone or null
   */
  async #subscribeBIS(apiKey, variantId, email, phone) {
    /** @type {string[]} */
    const channels = ['EMAIL'];
    if (phone) channels.push('SMS');

    /** @type {Record<string, string>} */
    const profileAttributes = { email };
    if (phone) profileAttributes.phone_number = phone;

    const response = await fetch(
      'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(apiKey),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-10-15'
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels,
              profile: {
                data: {
                  type: 'profile',
                  attributes: profileAttributes
                }
              }
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: '$shopify:::$default:::' + variantId
                }
              }
            }
          }
        })
      }
    );

    if (!response.ok && response.status !== 202) {
      const data = await response.json().catch(() => null);
      const msg = data?.errors?.[0]?.detail || 'Something went wrong. Please try again.';
      throw new Error(msg);
    }
  }

  /**
   * Subscribes to a Klaviyo marketing list — fire and forget.
   * @param {string} apiKey
   * @param {string} listId
   * @param {string|null} email
   * @param {string|null} phone
   * @param {string} customSource
   */
  #subscribeToList(apiKey, listId, email, phone, customSource) {
    /** @type {Record<string, string>} */
    const profileAttributes = {};
    if (email) profileAttributes.email = email;
    if (phone) profileAttributes.phone_number = phone;

    fetch(
      'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(apiKey),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-10-15'
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: customSource,
              profile: {
                data: {
                  type: 'profile',
                  attributes: profileAttributes
                }
              }
            },
            relationships: {
              list: {
                data: { type: 'list', id: listId }
              }
            }
          }
        })
      }
    ).catch((error) => {
      console.error('[Notify Me] List subscription error:', error);
    });
  }

  /**
   * Resets the form back to its initial state (clears success/error, shows form).
   */
  reset() {
    if (this.refs.form) {
      this.refs.form.hidden = false;
      this.refs.form.reset();
    }
    if (this.refs.errorMessage) this.refs.errorMessage.hidden = true;
    if (this.refs.successMessage) this.refs.successMessage.hidden = true;
    if (this.refs.submitButton) this.refs.submitButton.disabled = false;
    if (this.refs.buttonLabel) this.refs.buttonLabel.textContent = this.#originalLabel;
    if (this.refs.legalText) this.refs.legalText.hidden = false;
  }

  /**
   * Identifies the user with Klaviyo JS for onsite tracking.
   * @param {string} email
   */
  #klaviyoIdentify(email) {
    try {
      // @ts-ignore — window.klaviyo is loaded externally
      if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
        // @ts-ignore
        window.klaviyo.identify({ $email: email });
      }
    } catch {
      // Klaviyo SDK may throw on identify — non-critical, BIS subscription already handled
    }
  }

  /**
   * Gets the formatted phone number from the input, or null.
   * @returns {string|null}
   */
  #getFormattedPhone() {
    const raw = this.refs.phoneInput?.value?.trim();
    if (!raw) return null;
    return formatE164(raw);
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    if (this.refs.errorMessage) {
      this.refs.errorText.textContent = message;
      this.refs.errorMessage.hidden = false;
    }
    if (this.refs.successMessage) this.refs.successMessage.hidden = true;
  }

  #showSuccess() {
    if (this.refs.form) this.refs.form.hidden = true;
    if (this.refs.errorMessage) this.refs.errorMessage.hidden = true;
    if (this.refs.successMessage) this.refs.successMessage.hidden = false;
    if (this.refs.legalText) this.refs.legalText.hidden = true;
  }
}

/**
 * Formats a phone number to E.164 format (US/CA numbers).
 * @param {string} input - Raw phone number string
 * @returns {string|null} E.164 formatted number or null if invalid
 */
const formatE164 = (input) => {
  const digits = input.replace(/\D/g, '');

  if (digits.length === 10) return '+1' + digits;
  if (digits.length === 11 && digits.startsWith('1')) return '+' + digits;
  if (input.startsWith('+') && digits.length >= 10 && digits.length <= 15) return '+' + digits;

  return null;
};

if (!customElements.get('notify-me-form')) {
  customElements.define('notify-me-form', NotifyMeForm);
}
